import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X, Globe } from "lucide-react";
import Logo from "./Logo";
import { navItems } from "../mock/mock";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const go = (path) => {
    setOpenMenu(null);
    setMobileOpen(false);
    navigate(path);
  };

  const openNow = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 180);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome || mobileOpen;
  const light = !solid; // white text over hero

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "bg-white shadow-sm py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 flex items-center justify-between">
        <Logo light={light} />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && openNow(item.label)}
              onMouseLeave={() => item.children && closeSoon()}
            >
              <button
                type="button"
                onClick={() => go(item.path)}
                className={`flex items-center gap-1 text-[13px] font-semibold tracking-wide uppercase transition-colors ${
                  light ? "text-white/90 hover:text-white" : "text-oe-navy hover:opacity-70"
                }`}
              >
                {item.label}
                {item.children && <ChevronDown size={14} className="mt-0.5" />}
              </button>
              {item.children && openMenu === item.label && (
                <div className="absolute left-0 top-full pt-3">
                  <div className="bg-white shadow-xl rounded-md border border-gray-100 min-w-[220px] py-2 oe-reveal">
                    {item.children.map((c) => (
                      <button
                        type="button"
                        key={c.label}
                        onClick={() => go(c.path)}
                        className="block w-full text-left px-5 py-2.5 text-[13px] text-oe-navy hover:bg-gray-50 transition-colors"
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <span
            className={`flex items-center gap-1 text-[12px] font-medium ${
              light ? "text-white/80" : "text-muted-foreground"
            }`}
          >
            <Globe size={14} /> English/UK
          </span>
        </nav>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-1"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <X size={26} className="text-oe-navy" />
          ) : (
            <Menu size={26} className={light ? "text-white" : "text-oe-navy"} />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto">
          <nav className="px-6 py-4 flex flex-col">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-gray-100 py-1">
                <button
                  type="button"
                  onClick={() => go(item.path)}
                  className="block w-full text-left py-3 text-[15px] font-semibold text-oe-navy uppercase tracking-wide"
                >
                  {item.label}
                </button>
                {item.children && (
                  <div className="pl-4 pb-2 flex flex-col">
                    {item.children.map((c) => (
                      <button
                        type="button"
                        key={c.label}
                        onClick={() => go(c.path)}
                        className="text-left py-2 text-[13px] text-muted-foreground"
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
